import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link2, Unlink, Loader2, CheckCircle, ExternalLink } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function GSCConnect({ onConnect, onDisconnect }) {
  const [status, setStatus] = useState({ connected: false, loading: true });
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    checkStatus();
    
    // Check for connection result from URL params
    const params = new URLSearchParams(window.location.search);
    if (params.get('gsc_connected') === 'true') {
      toast.success("Google Search Console connected successfully!");
      checkStatus();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('gsc_error')) {
      toast.error(`GSC connection failed: ${params.get('gsc_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/gsc/status`, {
        withCredentials: true
      });
      setStatus({ ...response.data, loading: false });
      if (response.data.connected && onConnect) {
        onConnect();
      }
    } catch (error) {
      setStatus({ connected: false, loading: false });
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/gsc/auth/url`, {
        withCredentials: true
      });
      
      if (response.data.auth_url) {
        // Redirect to Google OAuth
        window.location.href = response.data.auth_url;
      } else if (response.data.error) {
        // OAuth not configured - show instructions
        toast.error(response.data.error);
        console.log("GSC Setup Instructions:", response.data.setup_instructions);
      } else {
        toast.error("Failed to get authorization URL");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate connection");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Google Search Console?")) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/gsc/disconnect`, {}, {
        withCredentials: true
      });
      setStatus({ connected: false, loading: false });
      toast.success("GSC disconnected");
      if (onDisconnect) onDisconnect();
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  if (status.loading) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Checking GSC status...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            status.connected ? 'bg-green-100' : 'bg-gray-100'
          }`}>
            {status.connected ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <Link2 className="w-5 h-5 text-gray-600" />
            )}
          </div>
          <div>
            <h3 className="font-medium">Google Search Console</h3>
            <p className="text-sm text-gray-500">
              {status.connected 
                ? "Connected - Real ranking data available"
                : "Connect to see your real rankings"}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {status.connected ? (
            <>
              <Badge variant="success" className="bg-green-100 text-green-700">
                Connected
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDisconnect}
                className="text-red-600 hover:bg-red-50"
              >
                <Unlink className="w-4 h-4 mr-1" />
                Disconnect
              </Button>
            </>
          ) : (
            <Button 
              onClick={handleConnect}
              disabled={connecting}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {connecting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ExternalLink className="w-4 h-4 mr-2" />
              )}
              Connect GSC
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
